import { useState, type FormEvent } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Seo from "@/components/feature/Seo";
import JsonLd from "@/components/feature/JsonLd";

const BASE_URL = "https://mangistart.github.io";
const FORM_URL = "https://readdy.ai/api/form/da08qtm859p3u981e14g";

const values = [
  {
    icon: "ri-customer-service-2-line",
    title: "专业内容",
    desc: "由编辑团队精心整理漫画分类大全与推荐大全，信息准确可靠。",
  },
  {
    icon: "ri-community-line",
    title: "覆盖全面",
    desc: "漫画类型大全与题材分类一网打尽，热门与冷门题材均衡收录。",
  },
  {
    icon: "ri-refresh-line",
    title: "实时更新",
    desc: "漫画排行榜大全与人气排行数据每日更新，紧跟市场热点。",
  },
  {
    icon: "ri-search-eye-line",
    title: "快速定位",
    desc: "通过分类导航与标签体系，帮你快速找到想看的漫画。",
  },
];

const testimonials = [
  {
    name: "漫画爱好者·小林",
    text: "以前找漫画全靠朋友推荐，有了漫途导航的分类大全后，按题材分类就能快速找到想看的作品，太方便了。",
    role: "资深漫迷",
  },
  {
    name: "内容编辑·阿哲",
    text: "排行榜和人气排行数据更新很及时，推荐的漫画质量都很高，是目前用过最顺手的漫画导航网站。",
    role: "行业编辑",
  },
  {
    name: "新人漫迷·小琪",
    text: "第一次知道漫画还有这么多类型大全，跟着推荐大全看了好多部神作，入坑不亏！",
    role: "新晋漫迷",
  },
];

export default function About() {
  const [formError, setFormError] = useState("");
  const [status, setStatus] = useState<"idle" | "success">("idle");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = String(fd.get("website_alt") ?? "").trim();
    const messageRaw = String(fd.get("message") ?? "");
    if (messageRaw.length > 500) {
      setFormError("留言内容请控制在 500 字以内");
      setStatus("idle");
      return;
    }
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }
    fd.delete("website_alt");
    const body = new URLSearchParams(fd as unknown as Record<string, string>);
    try {
      setFormError("");
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";
      const spamHit = /spam/i.test(responseText) || /spam/i.test(serverMsg);
      const ok = res.ok && parsed?.code === "OK" && !spamHit;
      if (ok) {
        setStatus("success");
        form.reset();
      } else {
        setFormError(serverMsg || "提交失败，请稍后再试");
        setStatus("idle");
      }
    } catch {
      setFormError("网络异常，请稍后再试");
      setStatus("idle");
    }
  }

  return (
    <>
      <Seo
        title="关于我们 - 漫途导航·漫画分类大全"
        description="了解漫途导航·漫画分类大全的品牌故事、专业口碑与联系方式。我们致力于打造全面、权威的漫画分类大全与漫画排行榜大全导航平台。"
        keywords="关于我们,漫画分类大全,漫画排行榜大全,漫画推荐大全,漫画导航"
      />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: "关于漫途导航·漫画分类大全",
          url: `${BASE_URL}/about`,
          mainEntity: {
            "@type": "Organization",
            name: "漫途导航",
            description: "漫画分类大全导航平台，汇集漫画类型大全、漫画题材分类、漫画排行榜大全与漫画推荐大全。",
            email: "hello@mantudh.com",
            telephone: "+86-400-888-9999",
            foundingDate: "2019",
          },
        }}
      />
      <div className="flex min-h-screen flex-col bg-background-50">
        <Navbar />
        <main className="flex-1">
          <Breadcrumb items={[{ label: "关于我们" }]} />

          {/* 品牌介绍 */}
          <section className="relative overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=stylized%20anime%20manga%20illustration%20of%20a%20cozy%20reading%20table%20stacked%20with%20colorful%20manga%20books%20and%20floating%20comic%20pages%2C%20warm%20orange%20and%20amber%20lighting%2C%20inviting%20artistic%20backdrop%2C%20high%20detail%20editorial%20concept%20art&width=1600&height=520&seq=about-hero-2026&orientation=landscape"
                alt="漫途导航品牌背景"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/65 to-black/30"></div>
            </div>
            <div className="relative z-10 mx-auto w-full max-w-7xl px-4 py-20 md:px-6 md:py-28">
              <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-3 py-1 text-xs text-white backdrop-blur">
                <i className="ri-book-2-line text-accent-300" />
                ABOUT US
              </span>
              <h1 className="mt-4 font-heading text-3xl text-white md:text-5xl">关于漫途导航</h1>
              <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/85 md:text-lg">
                漫途导航是一个专注打造<strong className="text-accent-300">漫画分类大全</strong>与
                <strong className="text-accent-300">漫画推荐大全</strong>的导航平台，立志成为每个漫画爱好者
                最可靠、最全面的漫画分类与排行榜大全指南。
              </p>
            </div>
          </section>

          {/* 品牌故事 */}
          <section className="mx-auto w-full max-w-7xl px-4 py-16 md:px-6">
            <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
              <div>
                <span className="text-sm font-medium text-accent-600">OUR STORY</span>
                <h2 className="mt-2 font-heading text-2xl text-foreground-900 md:text-3xl">我们的故事</h2>
                <p className="mt-5 text-sm leading-relaxed text-foreground-700 md:text-base">
                  漫途导航诞生于一群资深漫画爱好者的共同愿景——让「找漫画」这件事变得简单、高效且有趣。
                </p>
                <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                  我们深知，面对海量的漫画类型大全与题材分类，新手漫迷常常无从下手。因此，我们从漫画分类大全出发，
                  系统梳理每一种漫画题材，配合漫画排行榜大全与人气排行数据，为大家提供一份清晰可靠的阅读地图。
                </p>
                <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                  多年来，我们持续更新漫画推荐大全，倾听读者反馈，不断完善分类体系，努力让每一位漫迷都能在这里
                  找到打动自己的作品。
                </p>
                <div className="mt-8 grid grid-cols-3 gap-4">
                  <div className="rounded-lg border border-background-200/70 bg-background-50 p-4 text-center">
                    <p className="font-heading text-2xl text-primary-600">7</p>
                    <p className="mt-1 text-xs text-foreground-500">年深耕内容</p>
                  </div>
                  <div className="rounded-lg border border-background-200/70 bg-background-50 p-4 text-center">
                    <p className="font-heading text-2xl text-primary-600">50w+</p>
                    <p className="mt-1 text-xs text-foreground-500">忠实漫迷</p>
                  </div>
                  <div className="rounded-lg border border-background-200/70 bg-background-50 p-4 text-center">
                    <p className="font-heading text-2xl text-primary-600">1200+</p>
                    <p className="mt-1 text-xs text-foreground-500">收录漫画</p>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {values.map((v) => (
                  <div
                    key={v.title}
                    className="rounded-lg border border-background-200/70 bg-background-100 p-6"
                  >
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-500 text-xl text-background-50">
                      <i className={v.icon} />
                    </span>
                    <h3 className="mt-4 font-medium text-foreground-900">{v.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{v.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* 口碑 */}
          <section className="w-full bg-background-100" aria-labelledby="reputation-heading">
            <div className="mx-auto w-full max-w-7xl px-4 py-16 md:px-6">
              <div className="mx-auto max-w-2xl text-center">
                <span className="text-sm font-medium text-accent-600">REPUTATION</span>
                <h2 id="reputation-heading" className="mt-2 font-heading text-2xl text-foreground-900 md:text-3xl">
                  我们收到的口碑
                </h2>
                <p className="mt-3 text-sm text-foreground-600">
                  来自广大漫迷与行业伙伴的真实评价，是激励我们不断前行的动力。
                </p>
              </div>
              <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
                {testimonials.map((t) => (
                  <figure
                    key={t.name}
                    className="flex flex-col rounded-lg border border-background-200/70 bg-background-50 p-6"
                  >
                    <div className="flex items-center gap-1 text-accent-500">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <i key={s} className="ri-star-fill text-sm" />
                      ))}
                    </div>
                    <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-foreground-700">
                      “{t.text}”
                    </blockquote>
                    <figcaption className="mt-5 flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                        <i className="ri-user-3-line" />
                      </span>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                        <p className="text-xs text-foreground-500">{t.role}</p>
                      </div>
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>
          </section>

          {/* 联系我们 */}
          <section className="mx-auto w-full max-w-7xl px-4 py-16 md:px-6" aria-labelledby="contact-heading">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
              <div>
                <span className="text-sm font-medium text-accent-600">CONTACT</span>
                <h2 id="contact-heading" className="mt-2 font-heading text-2xl text-foreground-900 md:text-3xl">
                  联系我们
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                  无论你是漫画爱好者、内容创作者还是合作方，都欢迎通过以下方式与我们联系。我们会尽快回复你。
                </p>
                <ul className="mt-8 space-y-4">
                  <li className="flex items-center gap-4">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-100 text-lg text-primary-700">
                      <i className="ri-mail-line" />
                    </span>
                    <div>
                      <p className="text-sm text-foreground-500">邮箱</p>
                      <p className="text-sm font-medium text-foreground-900">hello@mantudh.com</p>
                    </div>
                  </li>
                  <li className="flex items-center gap-4">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent-100 text-lg text-accent-700">
                      <i className="ri-phone-line" />
                    </span>
                    <div>
                      <p className="text-sm text-foreground-500">电话</p>
                      <p className="text-sm font-medium text-foreground-900">400-888-9999</p>
                    </div>
                  </li>
                  <li className="flex items-center gap-4">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary-100 text-lg text-secondary-700">
                      <i className="ri-map-pin-2-line" />
                    </span>
                    <div>
                      <p className="text-sm text-foreground-500">地址</p>
                      <p className="text-sm font-medium text-foreground-900">中国·杭州 西湖创意园区</p>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="rounded-lg border border-background-200/70 bg-background-100 p-6 md:p-8">
                <h3 className="font-heading text-lg text-foreground-900">给我们留言</h3>
                {status === "success" && (
                  <p className="mt-3 flex items-center gap-2 rounded-md bg-secondary-100 px-4 py-3 text-sm text-secondary-800">
                    <i className="ri-checkbox-circle-line" />
                    感谢你的留言，我们会尽快与你联系。
                  </p>
                )}
                {formError && (
                  <p className="mt-3 flex items-center gap-2 rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-800">
                    <i className="ri-error-warning-line" />
                    {formError}
                  </p>
                )}
                <form
                  data-readdy-form="da08qtm859p3u981e14g"
                  className="mt-5 grid grid-cols-1 gap-4"
                  onSubmit={handleSubmit}
                >
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <label className="block">
                      <span className="text-sm text-foreground-700">您的称呼</span>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="请输入您的称呼"
                        className="mt-2 w-full rounded-md border border-background-300/70 bg-background-50 px-4 py-2.5 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                      />
                    </label>
                    <label className="block">
                      <span className="text-sm text-foreground-700">邮箱</span>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="you@example.com"
                        className="mt-2 w-full rounded-md border border-background-300/70 bg-background-50 px-4 py-2.5 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                      />
                    </label>
                  </div>
                  <label className="block">
                    <span className="text-sm text-foreground-700">主题</span>
                    <input
                      type="text"
                      name="subject"
                      required
                      placeholder="咨询 / 建议 / 合作"
                      className="mt-2 w-full rounded-md border border-background-300/70 bg-background-50 px-4 py-2.5 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                    />
                  </label>
                  <label className="block">
                    <span className="text-sm text-foreground-700">留言内容</span>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      placeholder="请在此输入您的留言（500 字以内）"
                      className="mt-2 w-full resize-none rounded-md border border-background-300/70 bg-background-50 px-4 py-2.5 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                    />
                  </label>
                  {/* 反垃圾蜜罐字段 */}
                  <input
                    type="text"
                    name="website_alt"
                    data-honeypot="true"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                  <button
                    type="submit"
                    className="mt-1 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    提交留言
                  </button>
                </form>
              </div>
            </div>
          </section>
        </main>
        <Footer />
        <BackToTop />
      </div>
    </>
  );
}